/**
 * Rate the draft projection without letting the answer become state.
 *
 * Deliberately OUTSIDE react-query, exactly as `previewCapability` is: the
 * live loop's response goes through `RATER_X_RECALCULATED`, which replaces
 * `coverageState`, `global`, `ratingOptions` and `totalPremium` with the
 * rater's echo. Routing the proposed rate through it would launder the
 * draft into committed state, and after one round-trip committed state IS
 * the draft. The answer is carried on the rate and shown over the draft by
 * `showRaterAnswer`, never written into it.
 */
import { useEffect, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { recalculateRaterX } from '../../../api';
import type { RecalculateRaterXPayload } from '../../../api/types/rater-x-preset';
import { RaterFieldCollector } from '../../../../../../agencies/quotes/PrimeX/utils/primeX.util';
import { componentStateKey } from '../../../transformers/componentCoverages';
import { toRecalculatePayload } from '../rater/toRecalculatePayload';
import { toRecalculatedPayload } from '../rater/toRecalculatedPayload';
import type { RaterAnswer } from './showRaterAnswer';
import type { UnderwriteQuoteState } from '../types';
import type { DraftRow } from './types';

/**
 * Shorter than `useTypingTimeout`'s 800ms, which is tuned for typing. A drag
 * settles the moment the thumb stops, and ticks arrive tens of ms apart.
 */
const RATE_DEBOUNCE_MS = 400;

export interface DraftRate {
  readonly status: 'idle' | 'pricing' | 'ok' | 'failed';
  readonly totalPremium: number | null;
  readonly baselineTotalPremium: number | null;
  readonly premiumByCoverage: Readonly<Record<string, number>>;
  /** Category subtotals, keyed by `Category.key`. */
  readonly premiumByCategory: Readonly<Record<string, CategoryPremium>>;
  /** The coverages and globals the rater returned for the draft it priced. */
  readonly answer?: RaterAnswer;
  /**
   * Why the rater would refer the draft to underwriting, across the top level
   * and every suite part. Empty unless the draft priced (`ok`).
   */
  readonly referralReasons: readonly string[];
}

/**
 * `failed` blocks as hard as `pricing`: the premium impact is not merely
 * pending but unknown.
 *
 * No reject counterpart, deliberately — rejecting is always safe and costs no
 * rater call, so disabling it would trap an underwriter behind a stuck rate.
 * `ChangeActions` takes no `rejectDisabled` prop so this cannot be tidied into
 * symmetry.
 */
export interface CategoryPremium {
  readonly premium: number | null;
  readonly aggregatePremium: number | null;
}

/**
 * The whole quote's premium movement, or undefined when it is not yet knowable.
 *
 * Reads `baselineTotalPremium` and nothing else, so the card and the banner
 * cannot disagree. The fallback for a quote nobody has re-rated lives in
 * `useDraftRate` itself — patching it here covered only this caller, and the
 * banner went on subtracting a baseline it did not have.
 */
export function netPremiumDelta(rate: DraftRate): number | undefined {
  if (
    rate.status !== 'ok' ||
    rate.totalPremium == null ||
    rate.baselineTotalPremium == null
  ) {
    return undefined;
  }
  return rate.totalPremium - rate.baselineTotalPremium;
}

/**
 * What the draft refers to underwriting that the committed quote does not.
 *
 * Empty unless the draft has priced: the reasons belong to the rows that were
 * rated, so a rate that is re-pricing, failed or idle must not hand on what it
 * still holds. Mirrors `netPremiumDelta`, so the status rule lives here once.
 *
 * A referral the quote already carries is not something THIS change did, and
 * listing it would blame the change for a state the underwriter was already in.
 */
export function newReferralReasons(
  rate: DraftRate,
  committed: readonly string[]
): readonly string[] {
  if (rate.status !== 'ok') {
    return [];
  }
  const held = new Set(committed);
  return rate.referralReasons.filter((reason) => !held.has(reason));
}

/**
 * Every referral reason in a rater response, across the top level and every
 * `components.*` entry. The committed quote and the draft both read through
 * this, so the card's "new referral" diff compares two lists built by one rule.
 *
 * `withCarrier` adds `carrierReferralReasons`, which the view shows on flagged
 * Zurich renewals. Leaving it off one side would list a carrier reason the
 * quote already carries as new, or miss one the draft newly causes.
 */
export function collectReferralReasons(
  data: object,
  withCarrier: boolean
): string[] {
  const raterData = data as Parameters<typeof RaterFieldCollector.collect>[0];
  const reasons = Array.from(
    RaterFieldCollector.collect(raterData, 'referralReasons')
  );
  if (!withCarrier) {
    return reasons;
  }
  const carrierReasons = Array.from(
    RaterFieldCollector.collect(raterData, 'carrierReferralReasons')
  );
  return Array.from(new Set([...reasons, ...carrierReasons]));
}

/**
 * The collector throws on a referral list that is not a list. This runs in
 * the response handler, where a throw lands in the catch and reports a good
 * price as `failed`; a malformed list is no reason to lose the price, so it
 * reads as no reasons.
 */
function readReferralReasons(
  data: object,
  withCarrier: boolean
): readonly string[] {
  try {
    return collectReferralReasons(data, withCarrier);
  } catch {
    return [];
  }
}

/**
 * Why accept is held, or null when it is not held.
 *
 * The gate and the words for it come from ONE function. They were two, and
 * the marker's tooltip said "Pricing…" over a rate that had FAILED — a figure
 * that is not coming, described as one that is on its way — while the banner
 * three inches above it correctly said the draft could not be priced. Defining
 * the boolean in terms of the reason means a third blocked status cannot
 * arrive with no explanation attached.
 */
export function acceptBlockedReason(rate: DraftRate): string | null {
  if (rate.status === 'pricing') {
    return 'Pricing…';
  }
  if (rate.status === 'failed') {
    return 'Could not be priced';
  }
  return null;
}

export function acceptBlocked(rate: DraftRate): boolean {
  return acceptBlockedReason(rate) !== null;
}

export const IDLE: DraftRate = {
  status: 'idle',
  totalPremium: null,
  baselineTotalPremium: null,
  premiumByCoverage: {},
  premiumByCategory: {},
  referralReasons: [],
};

// What was actually rated, alongside the result. Kept out of the public
// `DraftRate` shape — it exists only so render can tell whether `rate` is
// still describing the rows in front of it.
interface RateState extends DraftRate {
  readonly forSignature: string;
}

const IDLE_STATE: RateState = { ...IDLE, forSignature: '' };

/**
 * A stable description of what is staged. Rebuilding the payload on every
 * render would re-fire the rater on unrelated state changes; comparing row
 * objects by identity would too, since `projectDraft` rebuilds them each
 * replay.
 */
function rowsSignature(rows: readonly DraftRow[]): string {
  return rows
    .map(
      (row) =>
        `${row.entryId}:${row.fields
          .map(
            (field) =>
              `${field.path.join('.')}=${JSON.stringify(field.proposed)}`
          )
          .join(',')}`
    )
    .sort()
    .join('|');
}

export function useDraftRate(
  draftState: UnderwriteQuoteState,
  committedState: UnderwriteQuoteState,
  rows: readonly DraftRow[],
  /**
   * The committed premium to measure against when state has none.
   *
   * `committedState.totalPremium` is only set by `RATER_X_RECALCULATED`, so on a
   * quote nobody has re-rated since loading it there is nothing to subtract
   * from. Pass the rater query's own total — the figure Indicated Pricing
   * renders — and every consumer of this rate agrees, rather than each one
   * patching around the gap for itself.
   */
  fallbackBaseline?: number | null,
  /**
   * Whether referral reasons include `carrierReferralReasons`. Pass the same
   * value the committed list uses — see `collectReferralReasons`.
   */
  withCarrierReferrals = false
): DraftRate {
  const [rate, setRate] = useState<RateState>(IDLE_STATE);
  const signature = rowsSignature(rows);
  // `LimitField` feeds `TickedSlider`, which wires MUI `Slider`'s CONTINUOUS
  // `onChange` to `setCoverage` — a ten-tick drag is ten staged edits without
  // this. The committed loop is immune (a `useQuery` on a constant key will not
  // start a second fetch while one is in flight); this path calls the API
  // directly, so the debounce is its only protection.
  const [settledSignature] = useDebouncedValue(signature, RATE_DEBOUNCE_MS);
  const baseline = committedState.totalPremium ?? fallbackBaseline ?? null;
  // The reducer computed this during replay. Deriving it a second time here is
  // exactly the drift this design exists to remove.
  const strictMode = !draftState.globalChanged;

  useEffect(() => {
    // LIVE signature, not the debounced one: Reject All must clear instantly
    // and cost no rater call.
    if (signature === '') {
      setRate(IDLE_STATE);
      return undefined;
    }

    // Still settling; render already reports `pricing`.
    if (settledSignature !== signature) {
      return undefined;
    }

    let live = true;
    // `live` ignores the response; the abort is what stops a superseded drag
    // position still costing a full server rating.
    const controller = new AbortController();
    setRate((current) => ({
      ...current,
      status: 'pricing',
      referralReasons: [],
    }));

    const payload = toRecalculatePayload(draftState, strictMode);

    recalculateRaterX(draftState.quote.quote.id)(draftState.productType, {
      data: payload as RecalculateRaterXPayload,
      signal: controller.signal,
    })
      .then((response) => {
        if (!live) {
          return;
        }
        const priced = response.data.valid !== false;
        setRate({
          status: priced ? 'ok' : 'failed',
          totalPremium: response.data.totalPremium ?? null,
          baselineTotalPremium: baseline,
          premiumByCoverage: readCoveragePremiums(response.data),
          premiumByCategory: readCategoryPremiums(response.data),
          referralReasons: priced
            ? readReferralReasons(response.data, withCarrierReferrals)
            : [],
          answer: {
            priced: draftState,
            payload: toRecalculatedPayload(draftState, response.data),
          },
          forSignature: signature,
        });
      })
      .catch(() => {
        if (!live) {
          return;
        }
        setRate({
          ...IDLE,
          status: 'failed',
          baselineTotalPremium: baseline,
          forSignature: signature,
        });
      });

    return () => {
      live = false;
      controller.abort();
    };
    // `draftState` IS a dependency. It was omitted on the theory that its
    // identity churns every render — it does not: `projection` is memoised on
    // `[root, staged]`, so it changes only when state actually moves. Without
    // it, a committed-side input change that leaves `signature`, `baseline`
    // and `strictMode` alone kept pricing the previous projection, and Accept
    // All could commit against a premium that no longer described the quote.
  }, [
    draftState,
    signature,
    settledSignature,
    baseline,
    strictMode,
    withCarrierReferrals,
  ]);

  const { forSignature, answer, ...held } = rate;

  // A held result is trustworthy only for the rows it was computed for.
  // Decided at render, so there is no window where a stale result could show.
  // That goes for the answer too: a cap whose cause was just rejected must not
  // outlive it.
  const isStale = forSignature !== signature;
  const publicRate: DraftRate = isStale ? held : { ...held, answer };
  if (
    isStale &&
    (publicRate.status === 'ok' || publicRate.status === 'failed')
  ) {
    return { ...publicRate, status: 'pricing', referralReasons: [] };
  }

  // `idle` is unrepresentable whenever rows is non-empty. On the render right
  // after rows becomes non-empty — before the effect above has had a chance
  // to flip `status` — `rate` is still `IDLE_STATE`, and the check above only
  // catches a stale `ok`/`failed`. Consumers gate commit actions on
  // `status === 'pricing'` alone (see `ProposedChangesBanner`'s Accept All),
  // so leaving `idle` representable here would let a change commit before its
  // premium impact is known. An empty row set is the one case that
  // legitimately stays `idle` — that is what keeps reject-all free of a
  // rater round-trip.
  if (signature !== '' && publicRate.status === 'idle') {
    return { ...publicRate, status: 'pricing' };
  }

  return publicRate;
}

interface RateShapeCoverage {
  readonly key?: string;
  readonly ratingDetails?: { readonly premium?: number };
  readonly children?: readonly RateShapeCoverage[];
}

interface RateShapeGroup {
  readonly coverages?: readonly RateShapeCoverage[];
}

interface RateShapeRatingDetails {
  readonly premium?: number;
  readonly aggregatePremium?: number;
}

interface RateShapeCategory {
  readonly key?: string;
  readonly groups?: readonly RateShapeGroup[];
  readonly ratingDetails?: RateShapeRatingDetails;
}

interface RateShapeModifierCategory {
  readonly id?: string;
  readonly ratingDetails?: RateShapeRatingDetails;
}

/** The part of a rater response that carries coverages and modifiers. */
interface RateShapePreset {
  readonly coverageCategories?: readonly RateShapeCategory[];
  readonly rateModifierCategories?: readonly RateShapeModifierCategory[];
}

interface RateShape extends RateShapePreset {
  /** A suite's parts, each a preset of its own. */
  readonly components?: Readonly<Record<string, RateShapePreset>>;
}

/**
 * Every preset in a response with the way it files its items: the top level
 * under the item's own `key`, each suite part under `<key>.<part>`.
 *
 * Two parts routinely carry an item with the same raw key, so a response read
 * only at the top level misses a suite entirely and one read by raw key lets
 * the second part overwrite the first. This is the one place that decides which
 * presets there are and what their keys are called; both readers below walk it.
 */
function presetsOf(data: RateShape): readonly {
  readonly preset: RateShapePreset;
  readonly keyOf: (key: string) => string;
}[] {
  return [
    { preset: data, keyOf: (key) => key },
    ...Object.entries(data.components ?? {}).map(([productType, preset]) => ({
      preset,
      keyOf: (key: string) => componentStateKey(key, productType),
    })),
  ];
}

/**
 * Section subtotals from the same response. Coverage categories key on
 * `Category.key`, rate-modifier categories on their `id` — the identifiers
 * `CoverageGroup` and `UnderwriterRatingOptions` respectively have to hand. A
 * suite part's are filed under `<key>.<part>`, as its coverages are.
 * Both land in one map; the two id spaces do not collide in practice, and a
 * collision would only mean one section showing the other's subtotal rather
 * than anything reaching state.
 */
function readCategoryPremiums(
  data: RateShape
): Record<string, CategoryPremium> {
  const out: Record<string, CategoryPremium> = {};

  for (const { preset, keyOf } of presetsOf(data)) {
    const put = (
      id: string | undefined,
      details: RateShapeRatingDetails | undefined
    ) => {
      if (!id) {
        return;
      }
      out[keyOf(id)] = {
        premium: details?.premium ?? null,
        aggregatePremium: details?.aggregatePremium ?? null,
      };
    };

    for (const category of preset.coverageCategories ?? []) {
      put(category.key, category.ratingDetails);
    }

    for (const modifier of preset.rateModifierCategories ?? []) {
      put(modifier.id, modifier.ratingDetails);
    }
  }

  return out;
}

/**
 * Walks the rater's real per-coverage shape:
 * `coverageCategories[].groups[].coverages[]`, recursing into each
 * coverage's `children` for sub-coverages, and into each suite part's own.
 *
 * Keyed by the coverage's STATE id — what `state.coverageState` is keyed by,
 * and what every row addresses it with. On a single-rater quote that is the
 * coverage's `key`; on a suite it is `<key>.<part>`.
 */
function readCoveragePremiums(data: RateShape): Record<string, number> {
  const out: Record<string, number> = {};

  for (const { preset, keyOf } of presetsOf(data)) {
    const visit = (coverage: RateShapeCoverage | undefined): void => {
      if (!coverage) {
        return;
      }
      const premium = coverage.ratingDetails?.premium;
      if (coverage.key && typeof premium === 'number') {
        out[keyOf(coverage.key)] = premium;
      }
      for (const child of coverage.children ?? []) {
        visit(child);
      }
    };

    for (const category of preset.coverageCategories ?? []) {
      for (const group of category.groups ?? []) {
        for (const coverage of group.coverages ?? []) {
          visit(coverage);
        }
      }
    }
  }

  return out;
}
